import { useCallback, useContext, useState } from 'react';

import {
  useConfirmEmployeeSalaryMutation,
  useRevokeConfirmEmployeeSalaryMutation,
} from '@app/api/EmployeeSalary';
import { SUCCESS, SUCCESS_MESSAGE } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type { UseConfirmSalaryModalProps } from '@app/pages/Analytics/components/ConfirmSalaryModal/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useConfirmSalaryModal = ({
  selectedRowIndex,
  setConfirmed,
  setSelectedRowIndex,
  setIsOpen,
  fetchData,
  year,
  month,
  userId,
}: UseConfirmSalaryModalProps) => {
  const [comment, setComment] = useState('');

  const [confirmSalary] = useConfirmEmployeeSalaryMutation();
  const [revokeConfirm] = useRevokeConfirmEmployeeSalaryMutation();

  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const handleConfirmModalClose = useCallback(() => {
    setSelectedRowIndex && setSelectedRowIndex(null);
    setIsOpen(false);
    setComment('');
  }, [setIsOpen, setSelectedRowIndex]);

  const confirmSalaryRecord = useCallback(async () => {
    if (!userId) return;
    try {
      await confirmSalary({
        ActiveBranchId,
        data: {
          UserIds: [Number(userId)],
          Comment: comment,
          Year: Number(year),
          Month: Number(month),
        },
      }).unwrap();

      typeof selectedRowIndex === 'number' &&
        setConfirmed &&
        setConfirmed((prev) => ({ ...prev, [selectedRowIndex]: true }));
      fetchData && fetchData();
      showNotification(SUCCESS_MESSAGE, SUCCESS);
      handleConfirmModalClose();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    ActiveBranchId,
    comment,
    confirmSalary,
    fetchData,
    handleConfirmModalClose,
    month,
    selectedRowIndex,
    setConfirmed,
    showNotification,
    userId,
    year,
  ]);

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setComment(value);
    },
    []
  );

  const handleRevokeConfirm = useCallback(async () => {
    if (!userId) return;
    try {
      await revokeConfirm({
        ActiveBranchId,
        data: {
          UserIds: [Number(userId)],
          Year: Number(year),
          Month: Number(month),
        },
      }).unwrap();

      fetchData && fetchData();
      showNotification(SUCCESS_MESSAGE, SUCCESS);
      handleConfirmModalClose();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    ActiveBranchId,
    fetchData,
    handleConfirmModalClose,
    month,
    revokeConfirm,
    showNotification,
    userId,
    year,
  ]);

  return {
    comment,
    confirmSalaryRecord,
    handleConfirmModalClose,
    handleCommentChange,
    handleRevokeConfirm,
  };
};
